import type { ImageFileProps } from "@shared-kernel/domain/types/file-validation";
import { UploadButton } from "./upload-button";

type UploadButtonProps = React.ComponentProps<typeof UploadButton>;

interface ImageUploadButtonProps extends Omit<UploadButtonProps, "validate" | "accept"> {
  validate?: (imageProps: ImageFileProps) => string | null;
  accept?: string[];
}

const loadImage = (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.addEventListener("load", () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    });
    img.addEventListener("error", err => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    });
    img.src = objectUrl;
  });

const getImageFileProps = async (file: File): Promise<ImageFileProps> => {
  const img = await loadImage(file);
  return {
    fileSize: file.size,
    type: file.type,
    width: img.naturalWidth,
    height: img.naturalHeight,
  };
};

export const ImageUploadButton = ({ onFileSelect, validate, ...props }: ImageUploadButtonProps) => {
  const handleFileSelect = async (file: File) => {
    try {
      const imageProps = await getImageFileProps(file);
      const validationError = validate?.(imageProps);
      if (validationError) {
        props.onError?.(validationError);
        return;
      }
      onFileSelect(file);
    } catch {
      props.onError?.("Erreur lors du chargement de l'image");
    }
  };

  return <UploadButton {...props} onFileSelect={handleFileSelect} />;
};
